import { FormikProps } from 'formik'
import React, { useCallback, useEffect, useState } from 'react'

import { MediaSelector } from 'partials/MediaSelector'

import { AutoSaveForm, Form } from 'widgets/Form'

import { useFileSelect } from 'hooks/useFileSelect'

import { isFile } from 'utils/files/isFile'

import { AllowedFileType, SelectedFile } from 'types/file'
import { Media } from 'types/media'

interface Props<Values> {
  allowedFileType: AllowedFileType
  children: (mediaSelector: JSX.Element, props: FormikProps<Values>) => JSX.Element
  onContinue?: () => void
  initialValues: Values
  onValuesChange: (values: Partial<Values>) => void
  submitCopy?: string
  size?: 'oneThird' | 'half' | 'twoThird' | 'full'
}

export const BaseStepForm = <
  Values extends { file?: Media | SelectedFile; isAudioEnabled?: boolean },
>({
  allowedFileType,
  children,
  onContinue,
  initialValues,
  onValuesChange,
  submitCopy,
  size,
}: Props<Values>) => {
  const [isAudioEnabled, setIsAudioEnabled] = useState(initialValues?.isAudioEnabled ?? true)

  const { handleFileSelect, handleReset, selectedFile, selectedFileType, selectedFileUrl } =
    // @ts-expect-error This is known due to RN looking at the wrong type for web
    useFileSelect(initialValues.file)

  const toggleIsAudioEnabled = () => {
    setIsAudioEnabled(!isAudioEnabled)
  }

  const audioSwitchDisabled = initialValues.file ? !isFile(initialValues.file) : false

  const handleContinue = useCallback(
    (values: Values) => {
      onValuesChange({ ...values, isAudioEnabled, file: selectedFile })
      onContinue && onContinue()
    },
    [isAudioEnabled, onContinue, onValuesChange, selectedFile],
  )

  useEffect(() => {
    onValuesChange({ file: selectedFile } as Values)
  }, [onValuesChange, selectedFile])

  useEffect(() => {
    onValuesChange({ isAudioEnabled } as Values)
  }, [isAudioEnabled, onValuesChange])

  const mediaSelector = (
    <MediaSelector
      allowedFileType={allowedFileType}
      audioSwitchDisabled={audioSwitchDisabled}
      clearFile={handleReset}
      isAudioEnabled={isAudioEnabled}
      selectedFileType={selectedFileType}
      selectedFileUrl={selectedFileUrl}
      setFile={handleFileSelect}
      toggleIsAudioEnabled={toggleIsAudioEnabled}
      size={size}
    />
  )

  return (
    <Form<Values>
      initialValues={{
        ...initialValues,
      }}
      onSubmit={handleContinue}
      submitCopy={submitCopy}
    >
      {(props) => (
        <>
          {children(mediaSelector, props)}
          <AutoSaveForm onSave={onValuesChange} debounce={1000} />
        </>
      )}
    </Form>
  )
}
